import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ApplicationCreatedModal from "./ApplicationCreatedModal";
import ApplicationPendingModal from "./ApplicationPendingModal";
import SelfManagedGuarantorsModal from "../../components/SelfManagedGuarantorsModal/SelfManagedGuarantorsModal";
import SelfManagedCoTenantsModal from "../../components/SelfManagedCoTenantsModal/SelfManagedCoTenantsModal";
import { ApplicationMember } from "./ApplicationPeopleSummary";
import ClientIdentificationForm from "../../components/ClientIdentificationForm/ClientIdentificationForm";
import constants from "../../Utils/constants";
import { clearSignupPersistedState } from "../../components/Onboarding/signupEntrySource";

type PendingStage = "pending" | "selfManaged" | "selfManagedCoTenant" | null;

type Guarantor = { name: string; email?: string; relation?: string };
type GuarantorWithStatus = Guarantor & { completed: boolean };

type Props = {
  createdApp: {
    title: string;
    peopleCount: number;
    members?: ApplicationMember[];
    pendingGuarantors?: Guarantor[];
    pendingCoTenants?: Guarantor[];
  } | null;
  user: any;
  requiredClientIdentificationWorkspace?: any;
  pendingStage: PendingStage;
  setPendingStage: Dispatch<SetStateAction<PendingStage>>;
  onClose: () => void;
  refreshUser?: () => Promise<any>;
};

function normalizeEmail(v: unknown): string {
  return typeof v === "string" ? v.toLowerCase() : "";
}

function normalizeName(v: unknown): string {
  return typeof v === "string" ? v.trim().toLowerCase() : "";
}

function guarantorKey(g: Guarantor): string {
  return normalizeEmail(g.email) || normalizeName(g.name);
}

function isCompleted(
  g: Guarantor,
  added: any[],
  completedUsers: any[]
): boolean {
  const email = normalizeEmail(g.email);
  if (email) {
    if (
      completedUsers.some(
        (c) => c?.type === "guarantor" && normalizeEmail(c?.email) === email
      )
    ) {
      return true;
    }
    return added.some(
      (a) =>
        normalizeEmail(a?.gurantor_email) === email ||
        normalizeEmail(a?.guarantor_id?.email) === email
    );
  }

  const name = normalizeName(g.name);
  if (!name) return false;

  if (
    completedUsers.some(
      (c) => c?.type === "guarantor" && normalizeName(c?.name) === name
    )
  ) {
    return true;
  }

  return added.some((a) => {
    const addedName = normalizeName(
      `${a?.guarantor_id?.fname ?? ""} ${a?.guarantor_id?.lname ?? ""}`
    );
    return !!addedName && addedName === name;
  });
}

function getPending(
  createdApp: Props["createdApp"],
  user: any
): GuarantorWithStatus[] {
  const selected: Guarantor[] = (
    user?.tenant_extra_field_id?.selected_onboarding_users ?? []
  )
    .filter((u: any) => u?.type === "guarantor")
    .map((u: any) => ({
      name: u.name,
      email: u.email,
      relation: u.gurantor_relation ?? u.relation,
    }));

  const completedUsers: any[] =
    user?.tenant_extra_field_id?.completed_onboarding_users ?? [];
  const completed: Guarantor[] = completedUsers
    .filter((u: any) => u?.type === "guarantor")
    .map((u: any) => ({
      name: u.name,
      email: u.email,
      relation: u.gurantor_relation ?? u.relation,
    }));

  const base: Guarantor[] = createdApp?.pendingGuarantors ?? [
    ...selected,
    ...completed,
  ];

  const merged = new Map<string, Guarantor>();
  for (const g of base) {
    const key = guarantorKey(g);
    if (key && !merged.has(key)) merged.set(key, g);
  }
  if (createdApp?.pendingGuarantors) {
    for (const g of completed) {
      const key = guarantorKey(g);
      if (key && !merged.has(key)) merged.set(key, g);
    }
  }

  const raw: Guarantor[] = Array.from(merged.values());
  const added: any[] = user?.gurantor_added_id ?? [];

  return raw.map((g) => ({
    ...g,
    completed: isCompleted(g, added, completedUsers),
  }));
}

function isCompletedCoTenant(g: Guarantor, completedUsers: any[]): boolean {
  const email = normalizeEmail(g.email);
  if (
    email &&
    completedUsers.some((c: any) => normalizeEmail(c?.email) === email)
  )
    return true;
  const name = normalizeName(g.name);
  if (name && completedUsers.some((c: any) => normalizeName(c?.name) === name))
    return true;
  return false;
}

function getPendingCoTenants(
  createdApp: Props["createdApp"],
  user: any
): GuarantorWithStatus[] {
  const selected: Guarantor[] = (
    user?.tenant_extra_field_id?.selected_co_tenants ?? []
  )
    .filter((u: any) => u?.type === "co_tenant")
    .map((u: any) => ({ name: u.name, email: u.email }));
  const completedUsers: any[] =
    user?.tenant_extra_field_id?.completed_co_tenants ?? [];
  const completed: Guarantor[] = completedUsers.map((u: any) => ({
    name: u.name,
    email: u.email,
  }));
  const base: Guarantor[] = createdApp?.pendingCoTenants ?? [
    ...selected,
    ...completed,
  ];
  const merged = new Map<string, Guarantor>();
  for (const g of base) {
    const key = guarantorKey(g);
    if (key && !merged.has(key)) merged.set(key, g);
  }
  if (createdApp?.pendingCoTenants) {
    for (const g of completed) {
      const key = guarantorKey(g);
      if (key && !merged.has(key)) merged.set(key, g);
    }
  }
  const raw: Guarantor[] = Array.from(merged.values());
  return raw.map((g) => ({
    ...g,
    completed: isCompletedCoTenant(g, completedUsers),
  }));
}

export default function PostApplicationModals({
  createdApp,
  user,
  requiredClientIdentificationWorkspace,
  pendingStage,
  setPendingStage,
  onClose,
  refreshUser,
}: Props) {
  const navigate = useNavigate();
  const [clientIdentificationWorkspace, setClientIdentificationWorkspace] =
    useState<any>(null);

  const finishOnboarding = () => {
    const requiredWorkspace =
      requiredClientIdentificationWorkspace ||
      (user?.workspaceId?.client_identification_rule === true
        ? user.workspaceId
        : null);
    const requiresClientIdentification =
      user?.consents?.client_identification_consent?.status ===
        constants.PENDING || Boolean(requiredClientIdentificationWorkspace);
    if (
      requiresClientIdentification &&
      requiredWorkspace &&
      user?.consents?.client_identification_consent?.status !==
        constants.ACCEPTED
    ) {
      setClientIdentificationWorkspace(requiredWorkspace);
      return;
    }
    setPendingStage(null);
    onClose();
    navigate("/home?welcome=true");
  };

  const goHome = () => {
    clearSignupPersistedState();
    navigate("/home?welcome=true");
  };

  useEffect(() => {
    if (!createdApp) return;
    const pending = getPending(createdApp, user);
    const pendingCo = getPendingCoTenants(createdApp, user);
    if ((pending.length > 0 || pendingCo.length > 0) && !pendingStage)
      setPendingStage("pending");
  }, [createdApp, user, pendingStage, setPendingStage]);

  if (!createdApp) return null;

  if (clientIdentificationWorkspace) {
    return (
      <ClientIdentificationForm
        user={user}
        workspace={clientIdentificationWorkspace}
        onSubmitted={async () => {
          clearSignupPersistedState();
          await refreshUser?.();
          setPendingStage(null);
          onClose();
          navigate("/home?welcome=true");
        }}
      />
    );
  }

  const pending = getPending(createdApp, user);
  const pendingCo = getPendingCoTenants(createdApp, user);
  const pendingCount = pending.filter((g) => !g.completed).length;
  const pendingCoCount = pendingCo.filter((g) => !g.completed).length;
  const hasPending = pendingCount > 0;
  const hasCoPending = pendingCoCount > 0;
  const totalPending = pendingCount + pendingCoCount;

  if ((hasPending || hasCoPending) && pendingStage === "selfManaged") {
    const combined = [
      ...pending.map((p) => ({ ...(p as any), type: "guarantor" })),
      ...pendingCo.map((p) => ({ ...(p as any), type: "co_tenant" })),
    ];
    return (
      <div
        className="modal-fixed-screen"
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 80,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "var(--skor-color-canvas, #f6f6f6)",
          padding: "24px 16px",
        }}
      >
        <SelfManagedGuarantorsModal
          guarantors={combined as any}
          onLater={() => {
            finishOnboarding();
          }}
          inline
        />
      </div>
    );
  }

  if (hasCoPending && pendingStage === "selfManagedCoTenant") {
    return (
      <div
        className="modal-fixed-screen"
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 80,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "var(--skor-color-canvas, #f6f6f6)",
          padding: "24px 16px",
        }}
      >
        <SelfManagedCoTenantsModal
          coTenants={pendingCo}
          onLater={() => {
            finishOnboarding();
          }}
          inline
        />
      </div>
    );
  }

  if (hasPending || hasCoPending) {
    return (
      <ApplicationPendingModal
        title={createdApp.title}
        peopleCount={createdApp.peopleCount}
        members={createdApp.members}
        pendingCount={totalPending}
        onProceed={() =>
          setPendingStage(hasPending ? "selfManaged" : "selfManagedCoTenant")
        }
        onLater={() => {
          finishOnboarding();
        }}
      />
    );
  }

  return (
    <ApplicationCreatedModal
      title={createdApp.title}
      peopleCount={createdApp.peopleCount}
      members={createdApp.members}
      onClose={finishOnboarding}
    />
  );
}
